import { afterNextRender, ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { ErrorHandlerService } from '@core/errors/error-handler.service';
import { ModalType } from '@core/models/modal.types';
import { JexlService } from '@core/services/jexl.service';
import { ModalService } from '@core/services/modal.service';

import { SearchPharmacyModal } from '@features/pharmacy/modals/search-pharmacy/search-pharmacy.modal';
import { PharmacyService } from '@features/pharmacy/services/pharmacy.service';

import { Button } from '@shared/components/button/button';
import type { ButtonConfig } from '@shared/components/button/button.model';
import { Feedback } from '@shared/components/feedback/feedback';
import type { FeedbackConfig } from '@shared/components/feedback/feedback.types';
import { TaskCard } from '@shared/components/task-card/task-card';
import type { TaskCardConfig } from '@shared/components/task-card/task-card.types';

import type { PharmacyTaskCardConfig } from './pharmacy-task-card.types';

@Component({
  selector: 'app-pharmacy-task-card',
  templateUrl: './pharmacy-task-card.html',
  imports: [Button, Feedback, TaskCard],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PharmacyTaskCard {
  private readonly modalService = inject(ModalService);
  private readonly pharmacyService = inject(PharmacyService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly jexlService = inject(JexlService);

  readonly config = input.required<PharmacyTaskCardConfig>();

  protected readonly state = this.pharmacyService.state;
  protected readonly isLoading = computed(() => this.state().isLoading() || this.state().isInitial());
  protected readonly hasError = computed(() => this.state().isError());

  protected readonly errorFeedbackConfig = computed<FeedbackConfig>(() => {
    const error = this.state().getError();
    const errorContent = error ? this.errorHandlerService.getFeedbackConfig(error) : null;
    return {
      icon: 'alert-circle',
      iconColor: 'var(--p-red-500)',
      header: errorContent?.title ?? '',
      subhead: errorContent?.description,
    };
  });

  protected readonly errorButtonConfig = computed<ButtonConfig>(() => {
    const error = this.state().getError();
    const errorContent = error ? this.errorHandlerService.getFeedbackConfig(error) : null;
    return {
      label: errorContent?.buttonLabel,
      severity: 'secondary',
      rounded: true,
      fluid: true,
    };
  });

  protected readonly pharmacy = this.pharmacyService.pharmacy;

  protected readonly taskCardConfig = computed<TaskCardConfig>(() => {
    const pharmacy = this.pharmacy();
    const config = this.config();

    if (!pharmacy) {
      return config.emptyState ?? config.taskCard;
    }

    return this.jexlService.process(config.taskCard, { pharmacy });
  });

  constructor() {
    afterNextRender(() => {
      const currentState = this.state();
      if (!currentState.isSuccess() && !currentState.isLoading()) {
        this.pharmacyService.loadPharmacy();
      }
    });
  }

  protected onRetry(): void {
    this.pharmacyService.loadPharmacy();
  }

  protected async onButtonClick(): Promise<void> {
    await this.modalService.openModal(SearchPharmacyModal, {}, ModalType.CARD);
  }
}
